/* Reading an mbox without reading it whole: where messages start, what their
 * headers say, and how to get one message's bytes back out.
 *
 * **Pure functions over bytes**, so the node tests can drive every edge
 * (chunk boundaries, CRLF, quoting) without a worker or a browser. The worker
 * (`../worker.js`) is the only caller that touches a File.
 *
 * Proved in the spike first: `ideas/mboxray-spike/README.md`.
 */

import { addressParser, decodeWords } from "../vendor/postal-mime/postal-mime.js";

const F = 0x46, R = 0x72, O = 0x6f, M = 0x6d, SPACE = 0x20, LF = 0x0a, CR = 0x0d, GT = 0x3e;

const latin1 = new TextDecoder("latin1");
const utf8 = new TextDecoder("utf-8");
const cp1252 = new TextDecoder("windows-1252");

/** The offset of the line after `at`, or -1. The native indexOf is what makes a pass over gigabytes fast. */
export function nextLine(bytes, at) {
  const newline = bytes.indexOf(LF, at);
  return newline < 0 ? -1 : newline + 1;
}

/**
 * **A `From ` line separates messages only if it carries a year.** Careless
 * exporters leave body lines like "From here the road goes on" unescaped, and
 * a blank line before one proves nothing (fixture case 11). The envelope line
 * is `From <sender> <date>`, and every date format in the wild has a four
 * digit year.
 */
export function isSeparator(bytes, at) {
  if (bytes[at] !== F || bytes[at + 1] !== R || bytes[at + 2] !== O || bytes[at + 3] !== M || bytes[at + 4] !== SPACE) {
    return false;
  }

  let end = at;
  const limit = Math.min(bytes.length, at + 256);
  while (end < limit && bytes[end] !== LF) end++;

  return /^From \S+ +.*\b\d{4}\b/.test(latin1.decode(bytes.subarray(at, end)));
}

/**
 * The bytes from `at` to the end of the header block (the first blank line),
 * or null if the block does not end inside `bytes` within `limit` bytes. The
 * caller then reads the header on its own.
 */
export function headerBlock(bytes, at, limit = 256 * 1024, acceptTruncated = false) {
  const end = Math.min(bytes.length, at + limit);

  for (let i = bytes.indexOf(LF, at); i >= 0 && i < end - 1; i = bytes.indexOf(LF, i + 1)) {
    if (bytes[i + 1] === LF || (bytes[i + 1] === CR && bytes[i + 2] === LF)) {
      return bytes.subarray(at, i + 1);
    }
  }

  return acceptTruncated ? bytes.subarray(at, end) : null;
}

/**
 * One message's bytes as a mail parser wants them: the envelope line dropped,
 * mboxrd quoting undone (">From " back to "From ", one level per line), and
 * the blank line that separates messages in the file trimmed.
 *
 * mboxo quoting is not reversible, so a body line that genuinely began
 * ">From " comes back one character longer from an mboxo file. That is the
 * format's ambiguity, not ours.
 */
export function stripEnvelope(bytes) {
  const first = bytes.indexOf(LF);
  const body = first < 0 ? new Uint8Array(0) : bytes.subarray(first + 1);
  const out = new Uint8Array(body.length);
  let n = 0;

  for (let j = 0; j < body.length; j++) {
    if ((j === 0 || body[j - 1] === LF) && body[j] === GT) {
      let k = j;
      while (k < body.length && body[k] === GT) k++;

      if (body[k] === F && body[k + 1] === R && body[k + 2] === O && body[k + 3] === M && body[k + 4] === SPACE) {
        continue; // drop one '>'
      }
    }

    out[n++] = body[j];
  }

  while (n > 0 && (out[n - 1] === LF || out[n - 1] === CR)) n--;

  return out.subarray(0, n);
}

/**
 * Header bytes to text. Headers should be ASCII; real ones carry raw UTF-8,
 * and older ones raw Latin-1, so UTF-8 that does not decode cleanly is taken
 * to be windows-1252 (a superset browsers decode correctly; node does not).
 */
export function decodeHeaderBytes(bytes) {
  const text = utf8.decode(bytes);
  return text.includes("\ufffd") ? cp1252.decode(bytes) : text;
}

/** Unfolded headers, first occurrence wins, keys lower cased; plus the envelope line. */
export function parseHeaders(bytes) {
  const lines = decodeHeaderBytes(bytes).split(/\r?\n/);
  const envelope = lines[0].startsWith("From ") ? lines.shift() : "";
  const headers = {};
  let key = null;

  for (const line of lines) {
    if (/^[ \t]/.test(line) && key) {
      headers[key] += " " + line.trim();
      continue;
    }

    const colon = line.indexOf(":");
    if (colon <= 0) {
      key = null;
      continue;
    }

    key = line.slice(0, colon).trim().toLowerCase();
    const value = line.slice(colon + 1).trim();

    // A resent or forwarded message can repeat a field; the first is this one's.
    if (!(key in headers)) headers[key] = value;
    else key = null;
  }

  return { headers, envelope };
}

export function decode(value) {
  if (!value) return "";
  try {
    return decodeWords(value);
  } catch {
    return value;
  }
}

function firstAddress(value) {
  if (!value) return { name: "", address: "" };

  try {
    const [first] = addressParser(value);
    if (first?.group) return { name: decode(first.name || ""), address: first.group[0]?.address || "" };
    if (first) return { name: decode(first.name || ""), address: first.address || "" };
  } catch {
    // fall through to the raw value
  }

  return { name: decode(value), address: "" };
}

/** Message ids in angle brackets, in order: `References`, `In-Reply-To`. */
export function messageIds(value) {
  return value ? [...value.matchAll(/<([^<>\s]+)>/g)].map((match) => match[1]) : [];
}

/**
 * What the list needs about one message, from its header block alone.
 *
 * **The date falls back to the envelope line**, which carries the delivery
 * time, so a message with no `Date:` still sorts somewhere true (case 8).
 * **Attachments are a guess** (`multipart/mixed`): an exact answer needs the
 * message decoded, which is the cost the index exists to avoid.
 */
export function summarise(id, header, size) {
  const { headers, envelope } = parseHeaders(header);
  const from = firstAddress(headers["from"]);

  let date = headers["date"] ? Date.parse(headers["date"].replace(/\s*\([^)]*\)\s*$/, "")) : NaN;
  let dateFromEnvelope = false;

  if (Number.isNaN(date)) {
    const match = envelope.match(/^From \S+ +(.*)$/);
    date = match ? Date.parse(match[1]) : NaN;
    dateFromEnvelope = !Number.isNaN(date);
  }

  return {
    id,
    fromName: from.name,
    fromAddress: from.address,
    to: decode(headers["to"] || ""),
    cc: decode(headers["cc"] || ""),
    subject: decode(headers["subject"] || ""),
    date: Number.isNaN(date) ? null : date,
    dateFromEnvelope,
    labels: decode(headers["x-gmail-labels"] || ""),
    maybeAttachments: (headers["content-type"] || "").toLowerCase().startsWith("multipart/mixed"),
    messageId: messageIds(headers["message-id"])[0] || "",
    inReplyTo: messageIds(headers["in-reply-to"]),
    references: messageIds(headers["references"]),
    size,
  };
}
